import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  TextField,
  Box,
  Fab,
  Divider,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  fetchAgentById,
  updateAgent,
} from "../../../../../features/slices/Agent";
import Title from "../../../../../components/Title";
import { AppDispatch } from "../../../../../store";
import { useDispatch } from "react-redux";
import { useNotification } from "../../../../../context/NotificationContext";
import { useLoader } from "../../../../../context/LoaderContext";
import { JsonEditor } from "json-edit-react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Agent } from "../../../../../interfaces";

const AgentEdit = () => {
  const { agentId } = useParams();
  const [singleAgent, setAgent] = useState<Agent>({
    id: "",
    title: "",
    state: false,
    meta_data: {} as JSON,
    type: "",
    host: "",
    logic_group: "",
    subnet: "",
    UUID: "",
    os_type: "",
    created_at: new Date(),
    updated_at: new Date(),
  } as unknown as Agent);
  const { notify } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const fetchAgent = useCallback(
    debounce(async () => {
      showLoader();
      try {
        const result = await dispatch(fetchAgentById(Number(agentId)) as any);
        hideLoader();
        if (result.payload) {
          setAgent({ ...result.payload });
        }
      } catch (error) {
        hideLoader();
        console.error("Failed to fetch agent", error);
        notify("Failed to fetch agent", "error");
      }
    }, 300),
    [dispatch, agentId]
  );

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  const handleStateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgent((prevAgent) => ({
      ...prevAgent,
      state: event.target.checked,
    }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    showLoader();
    try {
      const result = await dispatch(
        updateAgent({
          id: agentId,
          title: singleAgent.title,
          state: singleAgent.state,
          meta_data: singleAgent.meta_data,
          host: singleAgent.host,
          UUID: singleAgent.UUID,
        })
      );
      if (result.type === "agents/updateAgent/fulfilled") {
        notify("Agent updated successfully", "success");
        hideLoader();
      } else {
        notify("Failed to update agent", "error");
        hideLoader();
      }
    } catch (error) {
      hideLoader();
      console.error("Failed to update agent", error);
      notify("Failed to update agent", "error");
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        mt: 1,
        width: "100%", maxWidth: 760,
        justifyContent: "center",
        margin: "auto",
        flexWrap: "wrap",
      }}
    >
      <Title>Edit Agent {agentId}</Title>
      <TextField
        margin="normal"
        fullWidth
        id="title"
        label="Title"
        name="title"
        value={singleAgent.title}
        InputProps={{
          readOnly: true,
        }}
      />

      <TextField
        margin="normal"
        fullWidth
        name="host"
        label="Host"
        type="text"
        id="host"
        value={singleAgent.host}
        InputProps={{
          readOnly: true,
        }}
      />

      <TextField
        margin="normal"
        fullWidth
        name="group"
        label="Group"
        type="text"
        id="group"
        value={singleAgent.logic_group}
        InputProps={{
          readOnly: true,
        }}
      />

      <TextField
        margin="normal"
        fullWidth
        name="subnet"
        label="Subnet"
        type="text"
        id="subnet"
        value={singleAgent.subnet}
        InputProps={{
          readOnly: true,
        }}
      />

      <TextField
        margin="normal"
        fullWidth
        name="UUID"
        label="UUID"
        type="text"
        id="UUID"
        value={singleAgent.UUID}
        InputProps={{
          readOnly: true,
        }}
      />

      <TextField
        margin="normal"
        fullWidth
        name="os_type"
        label="OS Type"
        type="text"
        id="os_type"
        value={singleAgent.os_type}
        InputProps={{
          readOnly: true,
        }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={singleAgent.state}
            onChange={handleStateChange}
            name="state"
            color="primary"
          />
        }
        label="State"
        sx={{ mt: 2 }}
      />

      <Divider sx={{ mt: 2, mb: 2 }} />
      <Typography fontWeight={"bold"}>Meta Data</Typography>
      <JsonEditor
        minWidth={"100%"}
        data={singleAgent.meta_data ?? {}}
        restrictEdit={true}
        restrictDelete={true}
        restrictAdd={true}
        collapse={1}
        onUpdate={(value) => setAgent({ ...singleAgent, meta_data: value })}
      />
      <Divider sx={{ mt: 2, mb: 2 }} />

      <Fab
        color="primary"
        variant="extended"
        sx={{ position: "fixed", bottom: 15, right: 15 }}
        onClick={handleSubmit}
        disabled={singleAgent.title === "" || singleAgent.host === ""}
      >
        Update Agent
      </Fab>
      <Fab
        color="primary"
        variant="extended"
        sx={{ position: "fixed", bottom: 15, left: 75 }}
        onClick={() => navigate(-1)}
      >
        Cancel
      </Fab>
    </Box>
  );
};

export default AgentEdit;
